<script setup>
import { FUSO_ESCRITORIO } from 'dashboard/helper/fusoEscritorio';
// ATENDIMENTO DAS LIGAÇÕES (telefonia, etapa E — 25/09/2026): filas, URAs e
// horários, e o CAMINHO de cada número do escritório (número → horário → URA
// → fila → ramais) desenhado como árvore. Mora em Integrações › Telefonia e,
// na etapa F, na aba Configuração da Central telefônica — o mesmo componente,
// pedido do Victor ("quero poder ver isso… tudo completo").
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TelefoniaAPI from 'dashboard/api/telefonia';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAbaPilula } from 'dashboard/routes/dashboard/legal/useAbaPilula';
import { corDoNome, iniciaisDoNome } from 'dashboard/helper/avatar';
import {
  abertoAgora,
  alvoDe,
  caminho,
  DIAS,
  ICONE,
  nomeDoRamal,
  partes,
  rotuloDe,
} from './destinos';
import CaminhoNo from './CaminhoNo.vue';
import SemanaDoHorario from './SemanaDoHorario.vue';
import TecladoDaUra from './TecladoDaUra.vue';
import FilaModal from './FilaModal.vue';
import UraModal from './UraModal.vue';
import HorarioModal from './HorarioModal.vue';

const props = defineProps({
  ramais: { type: Array, default: () => [] },
  operadoras: { type: Array, default: () => [] },
});
const roteamento = defineModel('roteamento', {
  type: Object,
  default: () => ({ filas: [], uras: [], horarios: [] }),
});

const { t } = useI18n();
const T = 'TELEFONIA.ROTEAMENTO';

const contexto = computed(() => ({
  ramais: props.ramais,
  filas: roteamento.value.filas || [],
  uras: roteamento.value.uras || [],
  horarios: roteamento.value.horarios || [],
}));

// ── Abas ────────────────────────────────────────────────────────────────────
const ABAS = [
  { id: 'caminho', icone: 'i-ph-git-fork-bold' },
  { id: 'filas', icone: 'i-ph-users-three-bold' },
  { id: 'uras', icone: 'i-ph-list-numbers-bold' },
  { id: 'horarios', icone: 'i-ph-clock-bold' },
];
const aba = ref('caminho');
const seg = ref(null);
const { estiloPilula, pilulaPronta, aoTrocarAba } = useAbaPilula(seg, {
  seletorAtivo: '.advup-seg__btn--on',
});
watch(aba, () => aoTrocarAba());

// ── O caminho de cada número ────────────────────────────────────────────────
const formatar = numero => {
  const d = String(numero || '');
  if (d.length === 11)
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d;
};
const numeros = computed(() =>
  props.operadoras.flatMap(o =>
    o.numeros.map(n => ({
      ...n,
      operadora: o.nome,
      arvore: caminho(n.destino, contexto.value, t),
    }))
  )
);

const contagem = id =>
  id === 'caminho' ? numeros.value.length : contexto.value[id].length;

// ── Resumos dos cartões ─────────────────────────────────────────────────────
const destinoTexto = d => rotuloDe(d, contexto.value, t);
const destinoIcone = d =>
  ICONE[alvoDe(d, contexto.value) ? partes(d)?.tipo : 'sumiu'] || ICONE.sumiu;
const ESTRATEGIA_ICONE = {
  ringall: 'i-ph-broadcast-bold',
  rrmemory: 'i-ph-arrows-clockwise-bold',
  leastrecent: 'i-ph-hourglass-medium-bold',
};
const nomeDe = r => nomeDoRamal(r, t);
const segundos = computed(() => t(`${T}.FILA.SEG`));
const membrosDa = f =>
  f.ramais.map(id => props.ramais.find(r => r.id === id)).filter(Boolean);
const faixaTexto = f => {
  const idx = f.dias.map(d => DIAS.indexOf(d)).sort((a, b) => a - b);
  const seguidos = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  const nomeDia = i => t(`${T}.HORARIO.DIAS.${DIAS[i]}`);
  const dias =
    seguidos && idx.length > 2
      ? `${nomeDia(idx[0])}–${nomeDia(idx[idx.length - 1])}`
      : idx.map(nomeDia).join(', ');
  return `${dias} · ${f.de}–${f.ate}`;
};
const agora = ref(new Date());
const relogio = setInterval(() => {
  agora.value = new Date();
}, 60000);
onBeforeUnmount(() => clearInterval(relogio));
const estaAberto = h => abertoAgora(h, agora.value);

// quem usa (o mesmo que o backend recusa ao apagar)
const usos = destino => {
  const c = contexto.value;
  return [
    ...numeros.value
      .filter(n => n.destino === destino)
      .map(n => formatar(n.numero)),
    ...c.filas.filter(f => f.senao === destino).map(f => f.nome),
    ...c.uras
      .filter(
        u => u.senao === destino || Object.values(u.opcoes).includes(destino)
      )
      .map(u => u.nome),
    ...c.horarios
      .filter(h => h.aberto === destino || h.fechado === destino)
      .map(h => h.nome),
  ];
};

// ── Salvar / apagar ─────────────────────────────────────────────────────────
const API = {
  filas: {
    criar: d => TelefoniaAPI.criarFila(d),
    atualizar: (id, d) => TelefoniaAPI.atualizarFila(id, d),
    apagar: id => TelefoniaAPI.apagarFila(id),
    destino: 'fila',
  },
  uras: {
    criar: d => TelefoniaAPI.criarUra(d),
    atualizar: (id, d) => TelefoniaAPI.atualizarUra(id, d),
    apagar: id => TelefoniaAPI.apagarUra(id),
    destino: 'ura',
  },
  horarios: {
    criar: d => TelefoniaAPI.criarHorario(d),
    atualizar: (id, d) => TelefoniaAPI.atualizarHorario(id, d),
    apagar: id => TelefoniaAPI.apagarHorario(id),
    destino: 'horario',
  },
};

const modal = ref(null); // { tipo, registro|null }
const salvando = ref(false);
const erroDoModal = ref('');
const aviso = ref('');
const abrir = (tipo, registro = null) => {
  erroDoModal.value = '';
  modal.value = { tipo, registro };
};

function receber(data) {
  roteamento.value = data.roteamento;
  aviso.value = data.aviso || '';
}

async function salvar(dados) {
  const { tipo, registro } = modal.value;
  salvando.value = true;
  erroDoModal.value = '';
  try {
    const { data } = registro
      ? await API[tipo].atualizar(registro.id, dados)
      : await API[tipo].criar(dados);
    receber(data);
    modal.value = null;
    useAlert(data.aviso ? t(`${T}.SALVO_COM_AVISO`) : t(`${T}.SALVO`));
  } catch (e) {
    erroDoModal.value = e?.response?.data?.error || t(`${T}.FALHOU`);
  } finally {
    salvando.value = false;
  }
}

const dialogo = ref(null);
const paraApagar = ref(null);
const ocupado = ref(false);
function pedirApagar(tipo, registro) {
  paraApagar.value = { tipo, registro };
  dialogo.value?.open();
}
async function apagar() {
  const { tipo, registro } = paraApagar.value;
  ocupado.value = true;
  try {
    const { data } = await API[tipo].apagar(registro.id);
    receber(data);
    dialogo.value?.close();
  } catch (e) {
    dialogo.value?.close();
    useAlert(e?.response?.data?.error || t(`${T}.FALHOU`));
  } finally {
    ocupado.value = false;
  }
}

// ── Forçar o horário ("chegou cedo" / "fechou antes", como no 3CX) ─────────
const forcando = ref(null); // { id, estado }
const horaCurta = iso =>
  new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: FUSO_ESCRITORIO,
  });
const PRAZOS = [
  { id: 'h1', horas: 1 },
  { id: 'h2', horas: 2 },
  { id: 'h4', horas: 4 },
  { id: 'dia' },
];
function ateDe(prazo) {
  if (prazo.horas) return new Date(Date.now() + prazo.horas * 3600000);
  const fim = new Date();
  fim.setHours(23, 59, 0, 0);
  return fim;
}
async function forcar(h, prazo) {
  try {
    const { data } = await TelefoniaAPI.forcarHorario(
      h.id,
      forcando.value.estado,
      ateDe(prazo).toISOString()
    );
    receber(data);
    useAlert(t(`${T}.HORARIO.FORCADO_OK`));
  } catch (e) {
    useAlert(e?.response?.data?.error || t(`${T}.FALHOU`));
  } finally {
    forcando.value = null;
  }
}
async function desforcar(h) {
  const { data } = await TelefoniaAPI.desforcarHorario(h.id);
  receber(data);
  useAlert(t(`${T}.HORARIO.NORMAL_OK`));
}

// clicar num passo da árvore abre a edição dele
const ABA_DO_TIPO = { fila: 'filas', ura: 'uras', horario: 'horarios' };
function editarPeloCaminho(destino) {
  const p = partes(destino);
  const registro = p && alvoDe(destino, contexto.value);
  if (registro && ABA_DO_TIPO[p.tipo]) abrir(ABA_DO_TIPO[p.tipo], registro);
}

const reenviando = ref(false);
async function reenviar() {
  reenviando.value = true;
  try {
    const { data } = await TelefoniaAPI.reenviarRoteamento();
    receber(data);
    useAlert(data.aviso ? t(`${T}.SALVO_COM_AVISO`) : t(`${T}.REENVIADO`));
  } catch (e) {
    useAlert(e?.response?.data?.error || t(`${T}.FALHOU`));
  } finally {
    reenviando.value = false;
  }
}

const NOVO = {
  filas: { icone: 'i-ph-users-three-fill', chave: 'NOVA_FILA' },
  uras: { icone: 'i-ph-list-numbers-fill', chave: 'NOVA_URA' },
  horarios: { icone: 'i-ph-clock-fill', chave: 'NOVO_HORARIO' },
};
</script>

<template>
  <section class="tel-rot">
    <header class="tel-rot__cab">
      <div class="min-w-0">
        <h2 class="tel-rot__tit">{{ t(`${T}.TIT`) }}</h2>
        <p class="tel-rot__sub">{{ t(`${T}.SUB`) }}</p>
      </div>
      <button
        v-if="aba !== 'caminho'"
        type="button"
        class="advup-btn-forte"
        @click="abrir(aba)"
      >
        <i :class="NOVO[aba].icone" aria-hidden="true" />
        {{ t(`${T}.${NOVO[aba].chave}`) }}
      </button>
    </header>

    <p v-if="aviso" class="tel-rot__aviso" role="alert">
      <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
      <span class="flex-1">{{ t(`${T}.AVISO`, { erro: aviso }) }}</span>
      <button
        type="button"
        class="advup-btn--vazado"
        :disabled="reenviando"
        @click="reenviar"
      >
        <i
          :class="
            reenviando ? 'i-ph-circle-notch advup-gira' : 'i-hugeicons-reload'
          "
          aria-hidden="true"
        />
        {{ t(`${T}.REENVIAR`) }}
      </button>
    </p>

    <div ref="seg" class="advup-seg advup-seg--pilula tel-rot__abas">
      <span
        class="advup-seg__pilula"
        :class="{ 'advup-seg__pilula--viva': pilulaPronta }"
        :style="estiloPilula"
        aria-hidden="true"
      />
      <button
        v-for="a in ABAS"
        :key="a.id"
        type="button"
        class="advup-seg__btn"
        :class="{ 'advup-seg__btn--on': aba === a.id }"
        :aria-pressed="aba === a.id"
        @click="aba = a.id"
      >
        <i :class="a.icone" aria-hidden="true" />
        {{ t(`${T}.ABAS.${a.id.toUpperCase()}`) }}
        <span class="tel-rot__conta">{{ contagem(a.id) }}</span>
      </button>
    </div>

    <Transition name="tel-rot-troca" mode="out-in">
      <!-- ── CAMINHO: a árvore de cada número ────────────────────────────── -->
      <div v-if="aba === 'caminho'" key="caminho" class="tel-rot__painel">
        <EstadoVazio
          v-if="!numeros.length"
          class="advup-card"
          icone="i-ph-git-fork"
          :titulo="t(`${T}.CAMINHO_VAZIO_TIT`)"
          :dica="t(`${T}.CAMINHO_VAZIO_DICA`)"
        />
        <article
          v-for="n in numeros"
          :key="n.id"
          class="advup-card tel-rot__caminho"
        >
          <div class="tel-rot__origem">
            <span class="tel-rot__origem-ico" aria-hidden="true">
              <i class="i-ph-phone-incoming-fill" />
            </span>
            <div class="min-w-0">
              <p class="tel-rot__origem-num">{{ formatar(n.numero) }}</p>
              <p class="tel-rot__origem-sub">
                {{ n.rotulo || n.operadora }}
                <span v-if="!n.ativo" class="ficha-pill ficha-pill--neutro">
                  {{ t(`${T}.DESLIGADO`) }}
                </span>
              </p>
            </div>
          </div>
          <div class="tel-rot__arvore">
            <CaminhoNo
              v-if="n.arvore"
              :no="n.arvore"
              @editar="editarPeloCaminho"
            />
          </div>
        </article>
      </div>

      <!-- ── FILAS ──────────────────────────────────────────────────────── -->
      <div v-else-if="aba === 'filas'" key="filas" class="tel-rot__painel">
        <EstadoVazio
          v-if="!contexto.filas.length"
          class="advup-card"
          icone="i-ph-users-three"
          :titulo="t(`${T}.VAZIO.FILAS_TIT`)"
          :dica="t(`${T}.VAZIO.FILAS_DICA`)"
        />
        <ul v-else class="tel-rot__grade">
          <li
            v-for="(f, n) in contexto.filas"
            :key="f.id"
            class="advup-card tel-rot__item tel-rot__item--fila advup-entra"
            :style="{ '--i': n }"
          >
            <div class="tel-rot__item-topo">
              <span class="tel-rot__ico" aria-hidden="true">
                <i class="i-ph-users-three-fill" />
              </span>
              <div class="min-w-0 flex-1">
                <p class="tel-rot__nome">{{ f.nome }}</p>
                <p class="tel-rot__meta">
                  <span class="tel-rot__estrategia">
                    <i
                      :class="ESTRATEGIA_ICONE[f.estrategia]"
                      aria-hidden="true"
                    />
                    {{ t(`${T}.FILA.ESTRATEGIAS.${f.estrategia}.NOME`) }}
                  </span>
                </p>
              </div>
              <div class="tel-rot__acoes">
                <button
                  type="button"
                  class="advup-acao"
                  :aria-label="t(`${T}.EDITAR`, { nome: f.nome })"
                  @click="abrir('filas', f)"
                >
                  <i class="i-hugeicons-pencil-edit-02" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  class="advup-acao advup-acao--perigo"
                  :aria-label="t(`${T}.APAGAR`, { nome: f.nome })"
                  @click="pedirApagar('filas', f)"
                >
                  <i class="i-hugeicons-delete-02" aria-hidden="true" />
                </button>
              </div>
            </div>
            <dl class="tel-rot__numeros">
              <div>
                <dt>{{ t(`${T}.FILA.TOQUE_CURTO`) }}</dt>
                <dd>
                  {{ f.toque }}<small>{{ segundos }}</small>
                </dd>
              </div>
              <div>
                <dt>{{ t(`${T}.FILA.ESPERA_CURTO`) }}</dt>
                <dd>
                  {{ f.espera }}<small>{{ segundos }}</small>
                </dd>
              </div>
              <div>
                <dt>{{ t(`${T}.FILA.DESCANSO_CURTO`) }}</dt>
                <dd>
                  {{ f.descanso || 0 }}<small>{{ segundos }}</small>
                </dd>
              </div>
            </dl>
            <ul class="tel-rot__pessoas">
              <li
                v-for="(r, i) in membrosDa(f)"
                :key="r.id"
                class="tel-rot__pessoa"
              >
                <span
                  v-if="f.estrategia === 'rrmemory'"
                  class="tel-rot__ordem"
                  >{{ i + 1 }}</span
                >
                <span
                  class="advup-avatar advup-avatar--p"
                  :class="`advup-avatar--${corDoNome(nomeDe(r))}`"
                  aria-hidden="true"
                >
                  {{ iniciaisDoNome(nomeDe(r)) }}
                  <img v-if="r.usuario?.foto" :src="r.usuario.foto" alt="" />
                </span>
                <span class="tel-rot__pessoa-nome">{{ nomeDe(r) }}</span>
                <span class="tel-rot__pessoa-num">{{ r.numero }}</span>
                <span
                  v-tooltip="
                    r.online ? t(`${T}.FILA.ONLINE`) : t(`${T}.FILA.OFFLINE`)
                  "
                  class="tel-rot__online"
                  :class="{ 'tel-rot__online--sim': r.online }"
                />
              </li>
              <li
                v-if="!membrosDa(f).length"
                class="advup-nota advup-nota--erro"
              >
                {{ t(`${T}.FILA.NINGUEM`) }}
              </li>
            </ul>
            <p v-if="f.mensagem || f.musica" class="tel-rot__tags">
              <span v-if="f.mensagem" class="tel-rot__tag">
                <i class="i-ph-waveform-bold" aria-hidden="true" />
                {{ t(`${T}.FILA.TAG_MENSAGEM`) }}
              </span>
              <span v-if="f.musica" class="tel-rot__tag">
                <i class="i-ph-music-notes-bold" aria-hidden="true" />
                {{ t(`${T}.FILA.TAG_MUSICA`) }}
              </span>
            </p>
            <p class="tel-rot__linha">
              <i class="i-ph-arrow-bend-down-right-bold" aria-hidden="true" />
              {{ t(`${T}.NINGUEM_ATENDE`) }}
              <span class="tel-rot__destino">
                <i :class="destinoIcone(f.senao)" aria-hidden="true" />
                {{ destinoTexto(f.senao) }}
              </span>
            </p>
            <p v-if="usos(`fila:${f.id}`).length" class="tel-rot__usos">
              {{
                t(`${T}.USADA_EM`, { onde: usos(`fila:${f.id}`).join(', ') })
              }}
            </p>
          </li>
        </ul>
      </div>

      <!-- ── URAs ───────────────────────────────────────────────────────── -->
      <div v-else-if="aba === 'uras'" key="uras" class="tel-rot__painel">
        <EstadoVazio
          v-if="!contexto.uras.length"
          class="advup-card"
          icone="i-ph-list-numbers"
          :titulo="t(`${T}.VAZIO.URAS_TIT`)"
          :dica="t(`${T}.VAZIO.URAS_DICA`)"
        />
        <ul v-else class="tel-rot__grade">
          <li
            v-for="(u, n) in contexto.uras"
            :key="u.id"
            class="advup-card tel-rot__item tel-rot__item--ura advup-entra"
            :style="{ '--i': n }"
          >
            <div class="tel-rot__item-topo">
              <span class="tel-rot__ico" aria-hidden="true">
                <i class="i-ph-list-numbers-fill" />
              </span>
              <div class="min-w-0 flex-1">
                <p class="tel-rot__nome">{{ u.nome }}</p>
                <p class="tel-rot__meta">
                  <template v-if="u.audio">
                    <i class="i-ph-waveform-bold" aria-hidden="true" />
                    {{ u.audio.nome }}
                  </template>
                  <span v-else class="tel-rot__sem-audio">{{
                    t(`${T}.URA.SEM_AUDIO`)
                  }}</span>
                  <span v-if="u.direto" class="tel-rot__tag">
                    <i class="i-ph-phone-outgoing-bold" aria-hidden="true" />
                    {{ t(`${T}.URA.TAG_DIRETO`) }}
                  </span>
                </p>
              </div>
              <div class="tel-rot__acoes">
                <button
                  type="button"
                  class="advup-acao"
                  :aria-label="t(`${T}.EDITAR`, { nome: u.nome })"
                  @click="abrir('uras', u)"
                >
                  <i class="i-hugeicons-pencil-edit-02" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  class="advup-acao advup-acao--perigo"
                  :aria-label="t(`${T}.APAGAR`, { nome: u.nome })"
                  @click="pedirApagar('uras', u)"
                >
                  <i class="i-hugeicons-delete-02" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div class="tel-rot__ura-corpo">
              <TecladoDaUra :opcoes="u.opcoes" :rotulo="destinoTexto" />
              <ul class="tel-rot__teclas">
                <li
                  v-for="k in Object.keys(u.opcoes).sort()"
                  :key="k"
                  class="tel-rot__tecla"
                >
                  <span class="tel-rot__digito">{{ k }}</span>
                  <i
                    :class="destinoIcone(u.opcoes[k])"
                    class="tel-rot__para-ico"
                    aria-hidden="true"
                  />
                  <span class="tel-rot__para">{{
                    destinoTexto(u.opcoes[k])
                  }}</span>
                </li>
              </ul>
            </div>
            <p class="tel-rot__linha">
              <i class="i-ph-arrow-bend-down-right-bold" aria-hidden="true" />
              {{ t(`${T}.SEM_TECLA_EM`, { s: u.espera }) }}
              <span class="tel-rot__destino">
                <i :class="destinoIcone(u.senao)" aria-hidden="true" />
                {{ destinoTexto(u.senao) }}
              </span>
            </p>
            <p v-if="usos(`ura:${u.id}`).length" class="tel-rot__usos">
              {{ t(`${T}.USADA_EM`, { onde: usos(`ura:${u.id}`).join(', ') }) }}
            </p>
          </li>
        </ul>
      </div>

      <!-- ── HORÁRIOS ───────────────────────────────────────────────────── -->
      <div v-else key="horarios" class="tel-rot__painel">
        <EstadoVazio
          v-if="!contexto.horarios.length"
          class="advup-card"
          icone="i-ph-clock"
          :titulo="t(`${T}.VAZIO.HORARIOS_TIT`)"
          :dica="t(`${T}.VAZIO.HORARIOS_DICA`)"
        />
        <ul v-else class="tel-rot__grade">
          <li
            v-for="(h, n) in contexto.horarios"
            :key="h.id"
            class="advup-card tel-rot__item tel-rot__item--horario advup-entra"
            :class="{ 'tel-rot__item--aberto': estaAberto(h) }"
            :style="{ '--i': n }"
          >
            <div class="tel-rot__item-topo">
              <span class="tel-rot__ico" aria-hidden="true">
                <i class="i-ph-clock-fill" />
              </span>
              <div class="min-w-0 flex-1">
                <p class="tel-rot__nome">{{ h.nome }}</p>
                <p class="tel-rot__meta">
                  <span
                    class="tel-rot__agora"
                    :class="{ 'tel-rot__agora--aberto': estaAberto(h) }"
                  >
                    <span class="tel-rot__pulso" aria-hidden="true" />
                    {{
                      estaAberto(h)
                        ? t(`${T}.HORARIO.AGORA_ABERTO`)
                        : t(`${T}.HORARIO.AGORA_FECHADO`)
                    }}
                  </span>
                </p>
              </div>
              <div class="tel-rot__acoes">
                <button
                  type="button"
                  class="advup-acao"
                  :aria-label="t(`${T}.EDITAR`, { nome: h.nome })"
                  @click="abrir('horarios', h)"
                >
                  <i class="i-hugeicons-pencil-edit-02" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  class="advup-acao advup-acao--perigo"
                  :aria-label="t(`${T}.APAGAR`, { nome: h.nome })"
                  @click="pedirApagar('horarios', h)"
                >
                  <i class="i-hugeicons-delete-02" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div class="tel-rot__forcar">
              <template v-if="h.forcado">
                <span
                  class="tel-rot__forcado"
                  :class="`tel-rot__forcado--${h.forcado.estado}`"
                >
                  <i class="i-ph-hand-palm-fill" aria-hidden="true" />
                  {{
                    t(
                      `${T}.HORARIO.FORCADO_${h.forcado.estado.toUpperCase()}`,
                      {
                        hora: horaCurta(h.forcado.ate),
                      }
                    )
                  }}
                </span>
                <button
                  type="button"
                  class="tel-rot__chip"
                  @click="desforcar(h)"
                >
                  {{ t(`${T}.HORARIO.VOLTAR_NORMAL`) }}
                </button>
              </template>
              <template v-else-if="forcando?.id === h.id">
                <span class="tel-rot__ate">
                  {{
                    forcando.estado === 'aberto'
                      ? t(`${T}.HORARIO.ABRIR_ATE`)
                      : t(`${T}.HORARIO.FECHAR_ATE`)
                  }}
                </span>
                <button
                  v-for="p in PRAZOS"
                  :key="p.id"
                  type="button"
                  class="tel-rot__chip tel-rot__chip--forte"
                  @click="forcar(h, p)"
                >
                  {{ t(`${T}.HORARIO.PRAZO.${p.id.toUpperCase()}`) }}
                </button>
                <button
                  type="button"
                  class="tel-rot__chip"
                  @click="forcando = null"
                >
                  {{ t(`${T}.CANCELAR`) }}
                </button>
              </template>
              <button
                v-else
                type="button"
                class="tel-rot__chip"
                @click="
                  forcando = {
                    id: h.id,
                    estado: estaAberto(h) ? 'fechado' : 'aberto',
                  }
                "
              >
                <i
                  :class="
                    estaAberto(h) ? 'i-ph-lock-bold' : 'i-ph-lock-open-bold'
                  "
                  aria-hidden="true"
                />
                {{
                  estaAberto(h)
                    ? t(`${T}.HORARIO.FECHAR_AGORA`)
                    : t(`${T}.HORARIO.ABRIR_AGORA`)
                }}
              </button>
            </div>
            <SemanaDoHorario
              :faixas="h.faixas"
              :agora="agora"
              :aberto="estaAberto(h)"
            />
            <ul class="tel-rot__faixas">
              <li v-for="(f, i) in h.faixas" :key="i">{{ faixaTexto(f) }}</li>
            </ul>
            <p v-if="h.feriados.length" class="tel-rot__meta">
              <i class="i-ph-calendar-x-fill" aria-hidden="true" />
              {{
                t(
                  `${T}.HORARIO.N_FERIADOS`,
                  { n: h.feriados.length },
                  h.feriados.length
                )
              }}
            </p>
            <div class="tel-rot__saidas">
              <p
                class="tel-rot__saida"
                :class="{ 'tel-rot__saida--agora': estaAberto(h) }"
              >
                <span class="tel-rot__saida-rot">
                  <i class="i-ph-sun-fill" aria-hidden="true" />
                  {{ t(`${T}.ABERTO`) }}
                </span>
                <span class="tel-rot__destino">
                  <i :class="destinoIcone(h.aberto)" aria-hidden="true" />
                  {{ destinoTexto(h.aberto) }}
                </span>
              </p>
              <p
                class="tel-rot__saida"
                :class="{ 'tel-rot__saida--agora': !estaAberto(h) }"
              >
                <span class="tel-rot__saida-rot">
                  <i class="i-ph-moon-stars-fill" aria-hidden="true" />
                  {{ t(`${T}.FECHADO`) }}
                </span>
                <span class="tel-rot__destino">
                  <i :class="destinoIcone(h.fechado)" aria-hidden="true" />
                  {{ destinoTexto(h.fechado) }}
                </span>
              </p>
            </div>
            <p v-if="usos(`horario:${h.id}`).length" class="tel-rot__usos">
              {{
                t(`${T}.USADA_EM`, { onde: usos(`horario:${h.id}`).join(', ') })
              }}
            </p>
          </li>
        </ul>
      </div>
    </Transition>

    <FilaModal
      v-if="modal?.tipo === 'filas'"
      :fila="modal.registro"
      :contexto="contexto"
      :salvando="salvando"
      :erro="erroDoModal"
      @close="modal = null"
      @salvar="salvar"
    />
    <UraModal
      v-if="modal?.tipo === 'uras'"
      :ura="modal.registro"
      :contexto="contexto"
      :salvando="salvando"
      :erro="erroDoModal"
      @close="modal = null"
      @salvar="salvar"
    />
    <HorarioModal
      v-if="modal?.tipo === 'horarios'"
      :horario="modal.registro"
      :contexto="contexto"
      :salvando="salvando"
      :erro="erroDoModal"
      @close="modal = null"
      @salvar="salvar"
    />
    <Dialog
      ref="dialogo"
      type="alert"
      icone="i-ph-trash"
      :title="t(`${T}.APAGAR_TIT`, { nome: paraApagar?.registro?.nome || '' })"
      :description="t(`${T}.APAGAR_TXT`)"
      :confirm-button-label="t(`${T}.APAGAR_CURTO`)"
      :is-loading="ocupado"
      @confirm="apagar"
    />
  </section>
</template>

<style scoped>
.tel-rot {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}
.tel-rot__cab {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--esp-3);
}
.tel-rot__tit {
  margin: 0;
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-12));
}
.tel-rot__sub {
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}
.tel-rot__aviso {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-3);
  border-radius: var(--r-3);
  font-size: var(--txt-nota);
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}
.tel-rot__abas {
  align-self: flex-start;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.tel-rot__abas .advup-seg__btn {
  gap: var(--esp-1);
  white-space: nowrap;
}
.tel-rot__conta {
  min-width: 18px;
  padding: 0 var(--esp-1);
  border-radius: var(--r-redondo);
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
  text-align: center;
  background: rgb(var(--slate-4));
  color: rgb(var(--slate-11));
}
.tel-rot__painel {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
}

/* ── Caminho ── */
.tel-rot__caminho {
  display: grid;
  grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
  gap: var(--esp-4);
  padding: var(--esp-4) var(--esp-5);
}
.tel-rot__origem {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-3);
}
.tel-rot__origem-ico {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--r-3);
  font-size: var(--ico-3);
  background: rgb(var(--teal-9));
  color: white;
  box-shadow: 0 4px 12px rgb(var(--teal-9) / 30%);
}
.tel-rot__origem-num {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgb(var(--slate-12));
}
.tel-rot__origem-sub {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-1);
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
.tel-rot__arvore {
  min-width: 0;
  padding-top: var(--esp-1);
}

/* ── Cartões ── */
.tel-rot__grade {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: var(--esp-3);
  margin: 0;
  padding: 0;
  list-style: none;
}
.tel-rot__item {
  --tom: var(--blue-9);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  padding: var(--esp-4);
  overflow: hidden;
  transition:
    transform var(--mov-2) var(--mov-curva),
    box-shadow var(--mov-2) var(--mov-curva);
}
.tel-rot__item:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px -12px rgb(var(--tom) / 35%);
}
/* o fio de cor no topo diz o TIPO (fila azul, URA íris) antes de ler o nome;
   no horário diz o ESTADO: teal aberto agora, grafite fechado */
.tel-rot__item::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, rgb(var(--tom)), rgb(var(--tom) / 20%));
}
.tel-rot__item--ura {
  --tom: var(--iris-9);
}
.tel-rot__item--horario {
  --tom: var(--slate-10);
}
.tel-rot__item--aberto {
  --tom: var(--teal-9);
}
.tel-rot__item-topo {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
}
.tel-rot__ico {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--r-3);
  font-size: var(--ico-3);
  color: #fff;
  background: linear-gradient(150deg, rgb(var(--tom)), rgb(var(--tom) / 75%));
  box-shadow: 0 6px 14px -6px rgb(var(--tom) / 70%);
}
.tel-rot__nome {
  margin: 0;
  overflow: hidden;
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tel-rot__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-1);
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}
.tel-rot__tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-1);
  margin: 0;
}
.tel-rot__tag {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: 1px var(--esp-2);
  border-radius: var(--r-redondo);
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
}
.tel-rot__forcar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-1);
}
.tel-rot__ate {
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
.tel-rot__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: 3px var(--esp-3);
  border-radius: var(--r-redondo);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-11));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-6));
  cursor: pointer;
  touch-action: manipulation;
  transition:
    background var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}
.tel-rot__chip:hover {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
}
.tel-rot__chip--forte {
  background: rgb(var(--teal-9));
  color: white;
  box-shadow: none;
}
.tel-rot__chip--forte:hover {
  background: rgb(var(--teal-10));
  color: white;
}
.tel-rot__forcado {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: 3px var(--esp-2);
  border-radius: var(--r-2);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
}
.tel-rot__forcado--fechado {
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}
.tel-rot__forcado--aberto {
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
}
.tel-rot__estrategia {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  font-weight: var(--peso-dado);
  color: rgb(var(--blue-11));
}
.tel-rot__numeros {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--esp-2);
  margin: 0;
}
.tel-rot__numeros > div {
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--slate-2));
}
.tel-rot__numeros dt {
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  text-transform: uppercase;
  letter-spacing: var(--rastro-rotulo);
  color: rgb(var(--slate-10));
}
.tel-rot__numeros dd {
  margin: 0;
  font-size: var(--txt-num);
  font-weight: var(--peso-num);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-12));
}
.tel-rot__numeros small {
  margin-left: var(--esp-1);
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-10));
}
.tel-rot__pessoas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding: 0;
  list-style: none;
}
.tel-rot__pessoa {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-3);
  transition: background var(--mov-1) var(--mov-curva);
}
.tel-rot__pessoa:hover {
  background: rgb(var(--slate-2));
}
.tel-rot__pessoa-nome {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-dado);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgb(var(--slate-12));
}
.tel-rot__pessoa-num {
  font-size: var(--txt-nota);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-10));
}
.tel-rot__destino {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  max-width: 100%;
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-redondo);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  background: rgb(var(--slate-3));
}
.tel-rot__destino > i {
  flex-shrink: 0;
  color: rgb(var(--slate-11));
}
.tel-rot__ura-corpo {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-3);
}
.tel-rot__ura-corpo .tel-rot__teclas {
  flex: 1;
  min-width: 0;
}
.tel-rot__para-ico {
  flex-shrink: 0;
  color: rgb(var(--slate-10));
}
.tel-rot__saidas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
}
.tel-rot__saida {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-1) var(--esp-1) var(--esp-1) var(--esp-2);
  border-radius: var(--r-redondo);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
.tel-rot__saida-rot {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
}
.tel-rot__saida--agora {
  background: rgb(var(--tom) / 10%);
  color: rgb(var(--slate-12));
}
.tel-rot__saida--agora .tel-rot__destino {
  color: #fff;
  background: rgb(var(--tom));
}
.tel-rot__saida--agora .tel-rot__destino > i {
  color: #fff;
}
.tel-rot__sem-audio {
  color: rgb(var(--amber-11));
}
.tel-rot__acoes {
  display: flex;
  gap: var(--esp-1);
}
.tel-rot__ordem {
  font-size: var(--txt-rotulo);
  color: rgb(var(--slate-10));
}
.tel-rot__online {
  width: 7px;
  height: 7px;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-7));
}
.tel-rot__online--sim {
  background: rgb(var(--ativo));
  box-shadow: 0 0 0 2px rgb(var(--ativo) / 25%);
}
.tel-rot__linha {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-1);
  margin: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
.tel-rot__linha > i {
  color: rgb(var(--slate-10));
}
.tel-rot__usos {
  margin: 0;
  padding-top: var(--esp-2);
  border-top: 1px dashed rgb(var(--slate-5));
  font-size: var(--txt-rotulo);
  color: rgb(var(--slate-10));
}
.tel-rot__teclas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding: 0;
  list-style: none;
}
.tel-rot__tecla {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
}
.tel-rot__digito {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--r-redondo);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  background: rgb(var(--iris-9));
  color: white;
}
.tel-rot__para {
  overflow: hidden;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tel-rot__faixas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--txt-nota);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-11));
}
.tel-rot__agora {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-11));
}
.tel-rot__pulso {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-8));
}
.tel-rot__agora--aberto {
  color: rgb(var(--teal-11));
}
.tel-rot__agora--aberto .tel-rot__pulso {
  background: rgb(var(--teal-9));
}
.tel-rot-troca-enter-active,
.tel-rot-troca-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
}
.tel-rot-troca-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.tel-rot-troca-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .tel-rot__forcado {
    animation: none;
  }
  .tel-rot__agora--aberto .tel-rot__pulso::after {
    animation: none;
  }
  .tel-rot__item:hover {
    transform: none;
  }
}
@media (max-width: 639px) {
  .tel-rot__caminho {
    grid-template-columns: 1fr;
    padding: var(--esp-4);
  }
  .tel-rot__grade {
    grid-template-columns: 1fr;
  }
  /* As quatro abas não cabiam lado a lado e "Horários" ficava cortada na
     borda (07/10/2026). Partes iguais, ícone sobre o rótulo — o mesmo da
     barra da Central — e a contagem vira selo no canto. */
  .tel-rot__abas {
    align-self: stretch;
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    overflow: visible;
  }
  .tel-rot__abas .advup-seg__btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-height: 48px;
    padding-inline: var(--esp-1);
  }
  .tel-rot__abas .advup-seg__btn i {
    font-size: 16px;
  }
  .tel-rot__abas .tel-rot__conta {
    position: absolute;
    top: 4px;
    right: 6px;
  }
}
</style>
